import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import {
  Popover,
  Tooltip,
  IconButton,
  Typography,
  Paper,
  Grid,
  TextField,
  makeStyles,
  MenuItem,
  Checkbox,
  ListItemText
} from "@material-ui/core";
import ReplayIcon from "@material-ui/icons/Replay";
import { FilterList } from "@material-ui/icons";

import { fetchSitesService } from "../../../services/dcm/sites";
import {
  ALL,
  createDefaultFilters,
  toFilterArray,
  nextFilterSelection,
  getLeadTimeDays
} from "../../../utils/placementFilters";

const useFilterStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    padding: "24px 24px 36px 24px",
    minWidth: "300px",
    maxWidth: "300px",
    overflow: "none"
  },
  header: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between"
  },
  title: {
    display: "inline-block",
    marginLeft: "7px",
    color: theme.palette.text.primary,
    fontSize: "14px",
    fontWeight: 500
  },
  select: {
    marginTop: "16px"
  },
  // Every filter is multi-select now, so the collapsed value can be several
  // entries long. Truncate rather than letting the popover grow.
  renderedValue: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  checkbox: {
    padding: "4px 8px 4px 0"
  }
}));

// Keeps a long option list from pushing the popover off screen.
const MENU_PROPS = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  PaperProps: { style: { maxHeight: 320 } }
};

export const Filter = ({ setFilters, filters }) => {
  const classes = useFilterStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const placementData = useSelector((state) => state.dcmPlacements.data);
  const placementInfo = useSelector((state) => state.placementInfo);
  const campaigns = useSelector((state) => state.dcmCampaigns);
  const [placementSites, setPlacementSites] = useState([]);

  function sentenceCase(input) {
    let word = input.slice(0, 1) + input.slice(1).toLowerCase();
    return word.replace("_", "").replace("_", " ");
  }

  function formatDate(input, isNumerical = true) {
    if (isNumerical) input = parseInt(input);

    const date = new Date(input);
    return [
      ("0" + (date.getUTCMonth() + 1)).slice(-2),
      ("0" + date.getUTCDate()).slice(-2),
      ("0" + date.getUTCFullYear()).slice(-2)
    ].join("/");
  }

  const getSitesByIds = async (data) => {
    if (data.length === 0) return [];
    let uniqueSiteIds = [];
    data.map((placement) => {
      if (!uniqueSiteIds.includes(placement.siteId)) {
        uniqueSiteIds.push(placement.siteId);
      }
      return placement;
    });
    return await fetchSitesService(uniqueSiteIds);
  };

  useEffect(() => {
    getSitesByIds(placementData)
      .then((sites) => setPlacementSites(sites))
      .catch((err) => console.log(err));
  }, [placementData]);

  // One definition per filter rather than nine near-identical blocks, so a
  // change to how multi-select behaves lands in one place.
  const filterDefs = useMemo(() => {
    const info = Object.values(placementInfo.byId);
    const unique = (values) => [...new Set(values)];

    return [
      {
        key: "status",
        label: "Status",
        options: [
          "Not submitted",
          "Default creative assigned",
          "Submitted"
        ].map((v) => ({ value: v, label: v }))
      },
      {
        key: "site",
        label: "Site",
        options: placementSites.map((s) => ({ value: s.id, label: s.name }))
      },
      {
        key: "start",
        label: "Start",
        options: unique(info.map((p) => p.Start_Date)).map((v) => ({
          value: v,
          label: formatDate(v, false)
        }))
      },
      {
        key: "end",
        label: "End",
        options: unique(info.map((p) => p.End_Date)).map((v) => ({
          value: v,
          label: formatDate(v, false)
        }))
      },
      {
        key: "leadTime",
        label: "Lead Time",
        options: unique(info.map((p) => getLeadTimeDays(p.Start_Date))).map(
          (v) => ({ value: v, label: String(v) })
        )
      },
      {
        key: "lastModified",
        label: "Last Modified",
        options: unique(
          placementData.map((p) => p.lastModifiedInfo.time)
        ).map((v) => ({ value: v, label: formatDate(v) }))
      },
      {
        key: "dimensions",
        label: "Dimensions",
        options: unique(
          placementData.map((p) => `${p.size.width} x ${p.size.height}`)
        ).map((v) => ({ value: v, label: v }))
      },
      {
        key: "compatibility",
        label: "Compatibility",
        options: unique(placementData.map((p) => p.compatibility)).map((v) => ({
          value: v,
          label: sentenceCase(v)
        }))
      },
      {
        key: "servingType",
        label: "Serving Type",
        options: unique(info.map((p) => p.Serving_Type || "N/A")).map((v) => ({
          value: v,
          label: v
        }))
      }
    ];
  }, [placementData, placementInfo.byId, placementSites]);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleFilter = (key, selected) => {
    setFilters({
      ...filters,
      [key]: nextFilterSelection(filters[key], selected)
    });
  };

  const handleFilterReset = useCallback(() => {
    setFilters(createDefaultFilters());
  }, [setFilters]);

  useEffect(() => {
    handleFilterReset();
  }, [campaigns.selectedId, handleFilterReset]);

  const renderFilter = (def) => {
    const selected = toFilterArray(filters[def.key]);
    const labelByValue = new Map(def.options.map((o) => [o.value, o.label]));

    return (
      <Grid className={classes.select} item xs={12} key={def.key}>
        <TextField
          select
          fullWidth
          variant="outlined"
          margin="dense"
          label={def.label}
          value={selected}
          onChange={(e) => handleFilter(def.key, e.target.value)}
          SelectProps={{
            multiple: true,
            MenuProps: MENU_PROPS,
            renderValue: (values) => (
              <div className={classes.renderedValue}>
                {values
                  .map((v) => {
                    if (v === ALL) return ALL;
                    const label = labelByValue.get(v);
                    return label === undefined ? v : label;
                  })
                  .join(", ")}
              </div>
            )
          }}
        >
          <MenuItem value={ALL}>
            <Checkbox
              className={classes.checkbox}
              color="primary"
              size="small"
              checked={selected.includes(ALL)}
            />
            <ListItemText primary={ALL} />
          </MenuItem>
          {def.options.map((option) => (
            <MenuItem key={String(option.value)} value={option.value}>
              <Checkbox
                className={classes.checkbox}
                color="primary"
                size="small"
                checked={selected.includes(option.value)}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    );
  };

  return (
    <>
      <Tooltip title="Filter" disableFocusListener>
        <IconButton onClick={handleOpen}>
          <FilterList />
        </IconButton>
      </Tooltip>
      <Popover
        disableScrollLock={true}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Paper className={classes.root}>
          <div className={classes.header}>
            <Typography className={classes.title} variant="h6">
              Filter Results
            </Typography>
            <Tooltip title="Reset All">
              <IconButton onClick={handleFilterReset}>
                <ReplayIcon />
              </IconButton>
            </Tooltip>
          </div>
          <Grid container>{filterDefs.map(renderFilter)}</Grid>
        </Paper>
      </Popover>
    </>
  );
};
